/**
 * useBackgroundPrefetch — once the visible view is ready, warm the code of the
 * tabs in the bar and read the one-shot data of the views this device uses
 * (docs/spec/01f-tab-loading.md, "Code warm-up from the tab bar" and
 * "Background data prefetch by usage").
 *
 * "Ready" is three things, in order: the gym's always-on data has arrived,
 * the active tab's chunk has rendered (the mount marker in `App.jsx`), and the
 * source layer's reads have settled — the visible view's own reads go first,
 * everything here comes after them. Then the next idle moment.
 *
 * Save-Data skips all of it. iOS Safari does not report Save-Data, so on an
 * iPhone it always runs.
 */
import { useEffect, useRef } from "react";
import { whenSourceReadsSettle } from "./useRangeCache";
import { readUsedViews, orderWarmupTabs, saveDataOn } from "../utils/tabUsage";

// Chunks are per page, not per gym: one warm-up per page load.
let chunksWarmed = false;

/** Test seam. */
export function _resetBackgroundPrefetch() { chunksWarmed = false; }

function nextIdle(timeoutMs = 2000) {
  return new Promise((resolve) => {
    if (typeof requestIdleCallback === "function") requestIdleCallback(() => resolve(), { timeout: timeoutMs });
    else setTimeout(resolve, 0);
  });
}

/**
 * Every warm-up `import()` swallows its error. A chunk nobody is rendering must
 * never reach the crash logger's stale-chunk handler, which would reload the
 * page under the user; a failed warm-up only means the chunk loads on demand
 * later, through the deadline-guarded `lazyChunk` path.
 */
function warm(load) {
  try { Promise.resolve(load()).catch(() => {}); } catch { /* ignore */ }
}

/**
 * @param {object} opts
 * @param {string|null} opts.gymId
 * @param {string}   opts.tab           the active tab
 * @param {string[]} opts.tabKeys       the tab bar's keys, in bar order
 * @param {boolean}  opts.dataLoaded    the always-on data has arrived
 * @param {string|null} opts.mountedView the tab whose chunk last rendered
 * @param {Object<string, Function>} opts.warmChunks  tab key → `() => import(…)`;
 *   `routeModal` is warmed for everyone
 * @param {Object<string, Function|undefined>} opts.prefetchers view key →
 *   `() => Promise` of one-shot reads; a view the viewer cannot open has none
 * @param {Function[]} [opts.always] one-shot reads that run first, whatever
 *   the usage and the active tab (the route-log cache)
 */
export function useBackgroundPrefetch({ gymId, tab, tabKeys, dataLoaded, mountedView, warmChunks, prefetchers, always = [] }) {
  const latest = useRef({});
  latest.current = { tab, tabKeys, warmChunks, prefetchers, always };
  // The gym the running prefetch belongs to. A tab switch must not stop it
  // halfway (the ready flag dips while the next tab's chunk loads); a gym
  // switch or an unmount does.
  const runGymRef = useRef(null);
  const gymRef = useRef(gymId);
  gymRef.current = gymId;
  useEffect(() => () => { runGymRef.current = null; }, []);

  const ready = !!gymId && dataLoaded && mountedView === tab;

  useEffect(() => {
    if (!ready || runGymRef.current === gymId) return;
    runGymRef.current = gymId;
    const isCancelled = () => runGymRef.current !== gymId || gymRef.current !== gymId;
    (async () => {
      await whenSourceReadsSettle();
      await nextIdle();
      if (isCancelled() || saveDataOn()) return;
      const used = readUsedViews(gymId);
      const now = latest.current;

      if (!chunksWarmed) {
        chunksWarmed = true;
        if (now.warmChunks.routeModal) warm(now.warmChunks.routeModal);
        for (const key of orderWarmupTabs(now.tabKeys, used, now.tab)) {
          if (now.warmChunks[key]) warm(now.warmChunks[key]);
        }
      }

      // Not tied to usage: what they feed is reachable from the default view.
      for (const run of latest.current.always || []) {
        if (isCancelled()) return;
        if (typeof run !== "function") continue;
        try { await run(); } catch { /* a prefetch is a convenience */ }
      }

      // One view after the other, so the prefetch never competes with itself.
      for (const view of used) {
        if (isCancelled()) return;
        const { tab: activeTab, prefetchers: fns } = latest.current;
        if (view === activeTab) continue;
        const run = fns && fns[view];
        if (typeof run !== "function") continue;
        try { await run(); } catch { /* a prefetch is a convenience */ }
      }
    })();
  }, [ready, gymId]);
}
